import type { LineItem } from '@fdc/__generated__/graphql';
import {
  GTM_EVENT_TYPES,
  PRODUCT_DETAIL_LIST_ID,
  PRODUCT_DETAIL_LIST_NAME,
} from '@fdc/constants/analytics-constants';
import {
  AddToCartEcommercePayload,
  ProductAnalyticsOptions,
  ViewItemListEcommerceItem,
} from '@fdc/constants/analytics-types';
import { ProductSummaryUnion } from '@fdc/types';
import { pushGtmAnalyticsEvent } from '@fdc/utils/analytics-utils';
import {
  CURRENCY as CART_CURRENCY,
  mapLineItemToAddToCartItem,
} from '@fdc/utils/cart-remove-from-cart-analytics';
import {
  getItemAttribution,
  getStoredProductAnalyticsOptions,
  resolveCartRemoveListAttribution,
  resolveListAttributionContext,
} from '@fdc/utils/item-attribution-analytics';
import { pushRemoveFromCartAnalyticsAfterSuccess } from '@fdc/utils/remove-from-cart-analytics';
import { mapProductSummaryToViewItemListItem } from '@fdc/utils/view-item-list-analytics';

const CURRENCY = 'NZD';

type AddToCartItemParams = {
  product: Partial<ProductSummaryUnion>;
  quantity: number;
  itemListId: string;
  itemListName: string;
  index: number;
  options?: ProductAnalyticsOptions;
};

type ShouldUseStoredAttributionOptionsParams = {
  itemListId: string;
  itemListName: string;
  productSku: string;
};

function shouldUseStoredAttributionOptions(
  params: ShouldUseStoredAttributionOptionsParams
): boolean {
  const stored = getItemAttribution();
  return (
    params.itemListId === PRODUCT_DETAIL_LIST_ID &&
    params.itemListName === PRODUCT_DETAIL_LIST_NAME &&
    stored !== null &&
    stored.item_id === params.productSku &&
    Boolean(
      stored.item_list_id && stored.item_list_name && stored.index != null
    )
  );
}

type MergeAddToCartAnalyticsOptionsParams = {
  itemListId: string;
  itemListName: string;
  productSku: string;
  options?: ProductAnalyticsOptions;
};

function mergeAddToCartAnalyticsOptions(
  params: MergeAddToCartAnalyticsOptionsParams
): ProductAnalyticsOptions {
  const storedOptions = shouldUseStoredAttributionOptions({
    itemListId: params.itemListId,
    itemListName: params.itemListName,
    productSku: params.productSku,
  })
    ? getStoredProductAnalyticsOptions(params.productSku)
    : undefined;

  return {
    ...params.options,
    sortBy: params.options?.sortBy ?? storedOptions?.sortBy,
    promotion: params.options?.promotion ?? storedOptions?.promotion,
    adId: params.options?.adId ?? storedOptions?.adId,
    cartSelectedUom: params.options?.cartSelectedUom,
    cartQuantity: params.options?.cartQuantity,
  };
}

function buildItemWithAttribution({
  product,
  quantity,
  itemListId,
  itemListName,
  index,
  options,
}: AddToCartItemParams): ViewItemListEcommerceItem {
  return {
    ...mapProductSummaryToViewItemListItem(
      product,
      index,
      itemListId,
      itemListName,
      options
    ),
    quantity,
  };
}

type BuildAddToCartPayloadParams = {
  product: Partial<ProductSummaryUnion>;
  quantityAdded: number;
  itemListId: string;
  itemListName: string;
  index: number;
  options?: ProductAnalyticsOptions;
};

export function buildAddToCartPayload(
  params: BuildAddToCartPayloadParams
): AddToCartEcommercePayload {
  const listContext = resolveListAttributionContext(
    params.itemListId,
    params.itemListName,
    params.index,
    params.product.sku ?? ''
  );
  const analyticsOptions = mergeAddToCartAnalyticsOptions({
    itemListId: params.itemListId,
    itemListName: params.itemListName,
    productSku: params.product.sku ?? '',
    options: params.options,
  });
  const item = buildItemWithAttribution({
    product: params.product,
    quantity: params.quantityAdded,
    itemListId: listContext.itemListId,
    itemListName: listContext.itemListName,
    index: listContext.itemListIndex,
    options: analyticsOptions,
  });
  const value = Math.max(0, item.price * params.quantityAdded);
  return {
    currency: CURRENCY,
    value,
    algolia_query_id: analyticsOptions.queryId ?? null,
    items: [item],
  };
}

type AddToCartMultipleItem = {
  product: Partial<ProductSummaryUnion>;
  quantityAdded: number;
  index: number;
};

type BuildAddToCartPayloadMultipleParams = {
  items: AddToCartMultipleItem[];
  itemListId: string;
  itemListName: string;
  options?: ProductAnalyticsOptions;
};

export function buildAddToCartPayloadMultiple(
  params: BuildAddToCartPayloadMultipleParams
): AddToCartEcommercePayload {
  const primarySku = params.items[0]?.product.sku ?? '';
  const listContext = resolveListAttributionContext(
    params.itemListId,
    params.itemListName,
    0,
    primarySku
  );
  const analyticsOptions = mergeAddToCartAnalyticsOptions({
    itemListId: params.itemListId,
    itemListName: params.itemListName,
    productSku: primarySku,
    options: params.options,
  });
  const ecommerceItems: ViewItemListEcommerceItem[] = params.items.map((item) =>
    buildItemWithAttribution({
      product: item.product,
      quantity: item.quantityAdded,
      itemListId: listContext.itemListId,
      itemListName: listContext.itemListName,
      index: item.index,
      options: analyticsOptions,
    })
  );
  const value = ecommerceItems.reduce(
    (sum, item) => sum + Math.max(0, item.price * item.quantity),
    0
  );
  return {
    currency: CURRENCY,
    value,
    algolia_query_id: analyticsOptions.queryId ?? null,
    items: ecommerceItems,
  };
}

type PushAddToCartAnalyticsAfterSuccessParams = {
  product: Partial<ProductSummaryUnion>;
  previousQuantity: number;
  newValue: number;
  itemListId: string;
  itemListName: string;
  itemListIndex: number;
  options?: ProductAnalyticsOptions;
};

/**
 * Fires on add/stepper click after optimistic cart sanitization (same timing as cart hooks).
 * Call from tiles and PDP wrappers, not from {@link AddToCartButton}.
 */
export function pushAddToCartAnalyticsAfterSuccess(
  params: PushAddToCartAnalyticsAfterSuccessParams
): void {
  const quantityAdded = params.newValue - params.previousQuantity;
  if (quantityAdded <= 0) {
    return;
  }

  pushAddToCartEvent(
    buildAddToCartPayload({
      product: params.product,
      quantityAdded,
      itemListId: params.itemListId,
      itemListName: params.itemListName,
      index: params.itemListIndex,
      options: params.options,
    })
  );
}

type PushCartQuantityChangeAnalyticsParams = {
  product: Partial<ProductSummaryUnion>;
  fromQty: number;
  toQty: number;
  itemListId: string;
  itemListName: string;
  itemListIndex: number;
  options?: ProductAnalyticsOptions;
};

export function pushCartQuantityChangeAnalytics(
  params: PushCartQuantityChangeAnalyticsParams
): void {
  if (params.toQty > params.fromQty) {
    pushAddToCartAnalyticsAfterSuccess({
      product: params.product,
      previousQuantity: params.fromQty,
      newValue: params.toQty,
      itemListId: params.itemListId,
      itemListName: params.itemListName,
      itemListIndex: params.itemListIndex,
      options: params.options,
    });
    return;
  }

  if (params.toQty < params.fromQty) {
    pushRemoveFromCartAnalyticsAfterSuccess({
      product: params.product,
      previousQuantity: params.fromQty,
      newValue: params.toQty,
      itemListId: params.itemListId,
      itemListName: params.itemListName,
      itemListIndex: params.itemListIndex,
      options: params.options,
    });
  }
}

export type AddToCartListAnalyticsContext = {
  itemListId: string;
  itemListName: string;
  itemListIndex?: number;
  analyticsOptions?: Omit<
    ProductAnalyticsOptions,
    'cartSelectedUom' | 'cartQuantity'
  >;
};

type PushCartLineAnalyticsParams = {
  product: Partial<ProductSummaryUnion>;
  listContext: AddToCartListAnalyticsContext;
  fromQty: number;
  toQty: number;
  cartSelectedUom: string | null | undefined;
};

export function pushAddToCartClickAnalytics(
  params: PushCartLineAnalyticsParams
): void {
  pushCartQuantityChangeAnalytics({
    product: params.product,
    fromQty: params.fromQty,
    toQty: params.toQty,
    itemListId: params.listContext.itemListId,
    itemListName: params.listContext.itemListName,
    itemListIndex: params.listContext.itemListIndex ?? 0,
    options: {
      ...params.listContext.analyticsOptions,
      cartSelectedUom: params.cartSelectedUom,
      cartQuantity: params.fromQty,
    },
  });
}

export function pushCartStepperChangeAnalytics(
  params: PushCartLineAnalyticsParams
): void {
  pushAddToCartClickAnalytics(params);
}

/**
 * Builds add_to_cart payload from a cart line item (cart page context).
 * Uses after-discount unit price; value = price × quantity added.
 */
export function buildAddToCartPayloadFromLineItem(
  lineItem: LineItem,
  quantityAdded: number,
  cartId: string
): AddToCartEcommercePayload {
  const { itemListId, itemListName } = resolveCartRemoveListAttribution(cartId);
  const item = mapLineItemToAddToCartItem({
    item: lineItem,
    quantity: quantityAdded,
    itemListId,
    itemListName,
  });
  const value = Math.max(0, item.price * quantityAdded);
  return {
    currency: CART_CURRENCY,
    value,
    algolia_query_id: null,
    items: [item],
  };
}

type PushAddToCartAnalyticsForLineItemParams = {
  lineItem: LineItem;
  previousQuantity: number;
  newQuantity: number;
  cartId: string;
};

/**
 * Pushes add_to_cart when a cart line quantity increased (stepper + or manual).
 */
export function pushAddToCartAnalyticsForLineItem({
  lineItem,
  previousQuantity,
  newQuantity,
  cartId,
}: PushAddToCartAnalyticsForLineItemParams): void {
  const quantityAdded = newQuantity - previousQuantity;
  if (quantityAdded <= 0) {
    return;
  }

  pushAddToCartEvent(
    buildAddToCartPayloadFromLineItem(lineItem, quantityAdded, cartId)
  );
}

export function pushAddToCartEvent(payload: AddToCartEcommercePayload): void {
  if (typeof window === 'undefined') {
    return;
  }
  pushGtmAnalyticsEvent({
    event: GTM_EVENT_TYPES.ADD_TO_CART,
    ecommerce: payload,
  });
}
